<script lang="ts" setup>
import ShimmerLoader from '@/components/common/animations/ShimmerLoader.vue'
import BadgedAvatar from '@/components/common/avatar/BadgedAvatar.vue'
import WebCamLiveModal from '@/components/golive/WebCamLiveModal/index.vue'
import { app } from '@/composition/app'
import { ChannelState } from '@/composition/channels'
import { CommentState } from '@/composition/comments'
import { EventState } from '@/composition/events'
import { GroupState } from '@/composition/groups'
import { PostState } from '@/composition/posts'
import { UserState } from '@/composition/users'
import { NO_POST_ICON } from '@/helper/const'
import { IPosts } from '@/models/Posts'
import { useGlobalStore } from '@/store/app'
import { isMobile } from '@/util/helper'
import { getAbbreviatedText } from '@/util/stringFormatting'
import { TEST_EMAILS, openNativeUrls } from '@ghl/clientportal-core'
import moment from 'moment-timezone'

import { Capacitor } from '@capacitor/core'
import { AppVersionsInstance } from '@ghl/clientportal-core'
import {
  ChevronDownIcon,
  ChevronLeftIcon,
  ChevronRightIcon,
  ChevronUpIcon,
  ClockIcon,
  EyeIcon,
  Globe01Icon,
  InfoCircleIcon,
  Lock03Icon,
  MoveIcon,
  Pin02Icon,
  VideoRecorderIcon,
  XCloseIcon,
} from '@gohighlevel/ghl-icons/24/outline'
import {
  UIButton,
  UICard,
  UIDivider,
  UIDrawer,
  UIDrawerContent,
  UITextLgMedium,
  UITextMdMedium,
  UITextMdNormal,
  UITextSmMedium,
  UITextSmNormal,
  UITooltip,
  useNotification,
} from '@gohighlevel/ghl-ui'
import { NAlert, NCarousel, NCarouselItem } from 'naive-ui'
import {
  computed,
  defineAsyncComponent,
  onBeforeUnmount,
  onMounted,
  onUpdated,
  reactive,
  ref,
  watch,
} from 'vue'
import { useI18n } from 'vue-i18n'
import { useRoute, useRouter } from 'vue-router'
import CommunityBottomSheet from '../common/CommunityBottomSheet.vue'
import { getFirstName, truncateString } from '../helper'

// async imports
const PostEditor = defineAsyncComponent(
  () => import('@/components/posts/PostEditor.vue')
)
const FeaturedPostCard = defineAsyncComponent(
  () => import('./FeaturedPostCard.vue')
)
const PostCard = defineAsyncComponent(
  () => import('./postcard/PostCardIndex.vue')
)
const LiveStreamModal = defineAsyncComponent(
  () => import('@/components/golive/GoLiveModal.vue')
)

const notification = useNotification()
const router = useRouter()
const appStore = useGlobalStore()
const route = useRoute()
const enableCreatePost = ref(false)
const carousalDragEnabled = ref(false)
const showCarousalRightArrow = ref(false)
const showCarousalLeftArrow = ref(false)
const currentCarousalIndex = ref(0)
const postEditorTrigger = ref<HTMLElement | null>(null)
const postContainerRef = ref<HTMLElement | null>(null)
const showFeaturedPost = ref(true)
const showFeaturedPostAbout = ref(false)
const showPinnedPosts = ref(true)
const upcomingEvent = ref<any>(null)
const lastChannelReadTimestamp = ref<string | Date | null>(null)
const lastUnreadPostCount = ref(0)
const currentCarouselIndex = ref(0)
const showLiveStreamModal = ref(false)
const showLiveStreamModalWithWebCam = ref(false)
const showUpdateModal = ref(false)

const openPlayStore = () => {
  openNativeUrls(
    'https://play.google.com/store/apps/details?id=net.clientclub.app.kollab&hl=en_IN'
  )
}

defineProps({
  showPopover: {
    type: Boolean,
    default: undefined,
  },
})
const carousel = ref<HTMLElement | null>(null)
const currentChannel = ref<any>(null)
const { t } = useI18n()
const isTestUser = computed(() => {
  return TEST_EMAILS.includes(UserState.state.currentUser?.email)
})

const posts = computed(() => {
  return PostState.state.posts
})

const pinnedPosts = computed(() => {
  if (route.params.channelSlug) {
    return (
      PostState.state.pinnedPosts.get(route.params.channelSlug as string) ?? []
    )
  } else if (route.params.groupSlug) {
    return (
      PostState.state.pinnedPosts.get(route.params.groupSlug as string) ?? []
    )
  } else {
    return undefined
  }
})

// we need this to update the carousel arrows visibility when pinned posts length changes
const pinnedPostsLength = computed(() => {
  if (route.params.channelSlug) {
    return PostState.state.pinnedPosts.get(route.params.channelSlug as string)
      ?.length
  } else if (route.params.groupSlug) {
    return PostState.state.pinnedPosts.get(route.params.groupSlug as string)
      ?.length
  } else {
    return undefined
  }
})

const showPostEditor = computed(
  () =>
    appStore.isAuthenticated &&
    GroupState.isActiveMember() &&
    route.name !== 'user-profile' &&
    route.name !== 'user-post-view'
)

onMounted(async () => {
  if (route?.fullPath?.includes('/users/')) {
    showPinnedPosts.value = false
  }

  // handle pinned posts skeleton loader

  if (pinnedPosts.value?.length === 0) {
    showPinnedPosts.value = false
  }

  //handleResize()
  if (route.params.channelSlug) {
    await Promise.all([
      !GroupState.state.allGroupsFetched
        ? GroupState.getGroupByLocation({ limit: 20 })
        : Promise.resolve(),
      GroupState.state.channelList.length === 0
        ? ChannelState.fetchChannels()
        : Promise.resolve(),
    ])
    const channelIndex = GroupState.state.channelList?.findIndex(ch => {
      return ch.slug === route.params.channelSlug
    })
    if (channelIndex != undefined) {
      if (channelIndex > -1) {
        currentChannel.value = ChannelState.setCurrentChannel(
          route.params.channelSlug as string
        )
        lastChannelReadTimestamp.value = currentChannel.value?.lastReadTimestamp
        lastUnreadPostCount.value = currentChannel.value?.unreadCount
        await Promise.all([getPinnedPostData(), getPostData()])
        ChannelState.markChannelAsRead(route.params.channelSlug as string)
      } else {
        notification.create({
          type: 'error',
          title: t('post.channelNotFound'),
          content: t('post.channelNotFoundMessage'),
          duration: 6000,
        })
        // redirect to home
        router.push({
          name: 'home',
          params: { groupSlug: route.params.groupSlug },
        })
      }
    }
  } else {
    await Promise.all([getPinnedPostData(), getPostData()])
  }

  carousel.value = document.querySelector('.n-carousel__slides')
  carousel.value?.addEventListener('wheel', verticalScrollCarousal)
  updateCarouselArrowsVisibility()

  window.addEventListener('scroll', handleScroll)
  //window.addEventListener('resize', handleResize)

  // Get events from start of today until 24 hours from now
  const currentDate = new Date()
  const startOfToday = new Date(currentDate)
  startOfToday.setHours(0, 0, 0, 0) // Set to beginning of today

  const oneDayFromNow = new Date(currentDate.getTime() + 24 * 60 * 60 * 1000)

  const upcomingEventResponse = await EventState.fetchEvents(
    startOfToday.toISOString(),
    oneDayFromNow.toISOString()
  )

  if (upcomingEventResponse?.events?.length > 0) {
    const userTimezone =
      UserState.state.currentUser?.timeZone || moment.tz.guess()
    const now = moment().tz(userTimezone)

    // Filter events based on test user
    const eligibleEvents = isTestUser.value
      ? upcomingEventResponse.events.filter(event => event.isFree === true)
      : upcomingEventResponse.events

    // Find currently ongoing event first
    let upcomingEventData = eligibleEvents.find(event => {
      // Convert event times to moment objects in event's timezone
      const startMoment = moment.tz(event.startTime, event.timezone)
      const endMoment = moment.tz(event.endTime, event.timezone)

      // Convert to user's timezone for comparison
      const startInUserTz = startMoment.clone().tz(userTimezone)
      const endInUserTz = endMoment.clone().tz(userTimezone)

      // For recurring events, check if the event's timezone had DST changes
      if (event.isRecurring && event.recurringMeta?.eventStart) {
        // Get the UTC offset difference between original event time and current time
        const originalEventMoment = moment.tz(
          event.recurringMeta.eventStart,
          event.timezone
        )
        const originalOffset = originalEventMoment.utcOffset()
        const currentOffset = moment.tz(event.timezone).utcOffset()

        // Only adjust if there was a DST change in the event's timezone
        if (originalOffset !== currentOffset) {
          const offsetDiff = (originalOffset - currentOffset) * 60 * 1000 // Convert minutes to milliseconds

          // Adjust start and end times by the offset difference
          const adjustedStart = startInUserTz
            .clone()
            .add(offsetDiff, 'milliseconds')
          const adjustedEnd = endInUserTz
            .clone()
            .add(offsetDiff, 'milliseconds')

          return now.isBetween(adjustedStart, adjustedEnd, null, '[]')
        }
      }

      // For non-recurring events or recurring events without DST changes
      return now.isBetween(startInUserTz, endInUserTz, null, '[]')
    })

    // If no ongoing event, find the next upcoming event within 24 hours
    if (!upcomingEventData) {
      upcomingEventData = eligibleEvents.find(event => {
        const startMoment = moment.tz(event.startTime, event.timezone)
        const startInUserTz = startMoment.clone().tz(userTimezone)

        // For recurring events, check if the event's timezone had DST changes
        if (event.isRecurring && event.recurringMeta?.eventStart) {
          // Get the UTC offset difference between original event time and current time
          const originalEventMoment = moment.tz(
            event.recurringMeta.eventStart,
            event.timezone
          )
          const originalOffset = originalEventMoment.utcOffset()
          const currentOffset = moment.tz(event.timezone).utcOffset()
          // Only adjust if there was a DST change in the event's timezone
          if (originalOffset !== currentOffset) {
            const offsetDiff = (originalOffset - currentOffset) * 60 * 1000

            const adjustedStart = startInUserTz
              .clone()
              .add(offsetDiff, 'milliseconds')
            return (
              adjustedStart.isAfter(now) &&
              adjustedStart.isBefore(moment(oneDayFromNow))
            )
          }
        }

        return (
          startInUserTz.isAfter(now) &&
          startInUserTz.isBefore(moment(oneDayFromNow))
        )
      })
    }

    if (upcomingEventData?.title) {
      const startMoment = moment.tz(
        upcomingEventData.startTime,
        upcomingEventData.timezone
      )
      const endMoment = moment.tz(
        upcomingEventData.endTime,
        upcomingEventData.timezone
      )
      const startInUserTz = startMoment.clone().tz(userTimezone)
      const endInUserTz = endMoment.clone().tz(userTimezone)

      let adjustedStartInUserTz = startInUserTz
      let adjustedEndInUserTz = endInUserTz

      // For recurring events, check if the event's timezone had DST changes
      if (
        upcomingEventData.isRecurring &&
        upcomingEventData.recurringMeta?.eventStart
      ) {
        // Get the UTC offset difference between original event time and current time
        const originalEventMoment = moment.tz(
          upcomingEventData.recurringMeta.eventStart,
          upcomingEventData.timezone
        )
        const originalOffset = originalEventMoment.utcOffset()
        const currentOffset = moment.tz(upcomingEventData.timezone).utcOffset()

        // Only adjust if there was a DST change in the event's timezone
        if (originalOffset !== currentOffset) {
          const offsetDiff = (originalOffset - currentOffset) * 60 * 1000

          // Adjust start and end times by the offset difference
          adjustedStartInUserTz = startInUserTz
            .clone()
            .add(offsetDiff, 'milliseconds')
          adjustedEndInUserTz = endInUserTz
            .clone()
            .add(offsetDiff, 'milliseconds')
        }
      }

      // Check if event is currently ongoing
      if (
        now.isBetween(adjustedStartInUserTz, adjustedEndInUserTz, null, '[]')
      ) {
        upcomingEvent.value = {
          title: upcomingEventData.title,
          startTime: t('post.isCurrentlyLive'),
        }
      } else {
        // Calculate time until event starts
        const duration = moment.duration(adjustedStartInUserTz.diff(now))
        const totalHours = Math.floor(duration.asHours())
        const minutes = duration.minutes()

        let formattedTime
        if (duration.asMinutes() < 1) {
          formattedTime = t('post.isStartingNow')
        } else if (duration.asHours() < 1) {
          // Less than 1 hour away
          formattedTime = t('post.isHappeningInMinutes', {
            minutes: Math.floor(duration.asMinutes()),
          })
        } else {
          // For times >= 1 hour, show total hours
          if (minutes >= 30) {
            // Round up to next hour if 30+ minutes
            formattedTime = t('post.isHappeningInHours', {
              hours: totalHours + 1,
              count: totalHours + 1,
            })
          } else {
            formattedTime = t('post.isHappeningInHours', {
              hours: totalHours,
              count: totalHours,
            })
          }
        }

        if (formattedTime) {
          upcomingEvent.value = {
            title: upcomingEventData.title,
            startTime: formattedTime,
          }
        }
      }
    }
  }
})

watch(
  () => GroupState.state.channelList,
  async (newVal, oldVal) => {
    // Only proceed if we're on a channel route and channelList just loaded for the first time
    if (!route.params.channelSlug) {
      return
    }

    // Only execute when channelList loads for the first time (oldVal is null/undefined)
    // This prevents duplicate calls when the watcher triggers multiple times
    if (!oldVal && newVal && newVal.length > 0) {
      const foundChannelIndex = GroupState.state.channelList?.findIndex(ch => {
        return ch.slug === route.params.channelSlug
      })

      if (foundChannelIndex > -1) {
        currentChannel.value = ChannelState.setCurrentChannel(
          route.params.channelSlug as string
        )
        lastChannelReadTimestamp.value = currentChannel.value?.lastReadTimestamp
        lastUnreadPostCount.value = currentChannel.value?.unreadCount
        await Promise.all([getPinnedPostData(), getPostData()])
        ChannelState.markChannelAsRead(route.params.channelSlug as string)
      } else {
        notification.create({
          type: 'error',
          title: t('post.channelNotFound'),
          content: t('post.channelNotFoundMessage'),
          duration: 6000,
        })
        // redirect to home
        router.push({
          name: 'home',
          params: { groupSlug: route.params.groupSlug },
        })
      }
    }
  }
)
watch(
  () => route.params,
  async (newParams, oldParams) => {
    if (
      oldParams?.channelSlug &&
      newParams?.channelSlug &&
      newParams?.channelSlug !== oldParams?.channelSlug
    ) {
      enableCreatePost.value = false
      const channelIndex = GroupState.state.channelList.findIndex(ch => {
        return ch.slug === newParams?.channelSlug
      })
      if (channelIndex > -1) {
        currentChannel.value = ChannelState.setCurrentChannel(
          newParams?.channelSlug as string
        )
        lastChannelReadTimestamp.value = currentChannel.value?.lastReadTimestamp
        lastUnreadPostCount.value = currentChannel.value?.unreadCount
        await Promise.all([getPinnedPostData(), getPostData()])
        // // update the carousel value when the channel changes
        // mark the channel as read
        ChannelState.markChannelAsRead(newParams?.channelSlug as string)
        carousel.value = document.querySelector('.n-carousel__slides')
        carousel.value?.addEventListener('wheel', verticalScrollCarousal)
        updateCarouselArrowsVisibility()
      } else {
        notification.create({
          type: 'error',
          title: t('post.channelNotFound'),
          content: t('post.channelNotFoundMessage'),
          duration: 6000,
        })
        // redirect to home
        router.push({
          name: 'home',
          params: { groupSlug: route.params.groupSlug },
        })
      }
    }
  }
)

watch(
  () => pinnedPostsLength.value,
  () => {
    if (pinnedPosts.value?.length === 0 || pinnedPosts.value == undefined) {
      showPinnedPosts.value = false
    } else {
      showPinnedPosts.value = true
    }
  }
)

const areMorePinnedPostAvailable = computed(() => {
  if (route.params.channelSlug) {
    return PostState.state.isNextPinnedPostsPageAvaialble.get(
      route.params.channelSlug as string
    )
  } else if (route.params.groupSlug) {
    return PostState.state.isNextPinnedPostsPageAvaialble.get(
      route.params.groupSlug as string
    )
  } else {
    return false
  }
})

watch(pinnedPostsLength, () => {
  trackPinnedPostCarousalIndex(currentCarousalIndex.value)
})
onUpdated(() => {
  if (carousel.value == null) {
    carousel.value = document.querySelector('.n-carousel__slides')
    carousel.value?.addEventListener('wheel', verticalScrollCarousal)
  }
})

// update editor position on window resize
// this function updates value of the post editor location variables do not delete this

onBeforeUnmount(() => {
  window.removeEventListener('scroll', handleScroll)
  // window.removeEventListener('resize', handleResize)
  carousel.value?.removeEventListener('wheel', verticalScrollCarousal)
})

function verticalScrollCarousal(event: WheelEvent) {
  const delta = event.deltaY || event.detail

  if (Math.abs(event.deltaX) > Math.abs(event.deltaY)) {
    return
  }

  if (
    delta < 0 &&
    window.scrollY >= 0 &&
    Math.abs(delta) > Math.abs(event.deltaX)
  ) {
    // scroll up
    window.scrollBy(0, delta)
    event.preventDefault()
    event.stopPropagation()
  } else if (
    delta > 0 &&
    window.scrollY >= 0 &&
    Math.abs(delta) > Math.abs(event.deltaX)
  ) {
    // scroll down
    window.scrollBy(0, delta)
    event.preventDefault()
    event.stopPropagation()
  }
}

async function handleScroll() {
  // Calculate the scroll position relative to the page height

  const scrollPending =
    document.documentElement.scrollHeight -
    window.pageYOffset -
    window.innerHeight
  // Check if scroll position is greater than or equal to 60%
  if (
    PostState.state.posts.length > 0 &&
    scrollPending <= 1400 &&
    !PostState.state.fetching
  ) {
    isLoading.value = true
    await PostState.fetchPosts(route, {
      limit: 20,
      previousId: PostState.state.posts[PostState.state.posts.length - 1]._id,
    })
    isLoading.value = false
  }
}

const isLoading = ref(false)
async function getPostData() {
  try {
    isLoading.value = true
    await PostState.fetchPosts(route, { limit: 20 })
    isLoading.value = false
  } catch (error: any) {
    isLoading.value = false
    if (error?.response?.status === 403) {
      console.log('getPostData :: 403 Forbidden error ::', {
        data: error.response.data,
        route: route.fullPath,
      })
      router.push({
        name: 'private-group',
        params: { groupSlug: route.params.groupSlug },
      })
    } else {
      router.push({
        name: 'home',
        params: { groupSlug: route.params.groupSlug },
      })
    }
  }
}

async function getPinnedPostData(lastPinnedPostIndexValue?: number) {
  try {
    if (route.fullPath.includes('/users/')) {
      return
    }
    if (route.params.channelSlug) {
      await PostState.fetchPinnedPostsForChannel(
        route.params.channelSlug as string,
        {
          limit: 4,
          previousPaginationKeyValue: lastPinnedPostIndexValue,
          sort: 'descending',
        }
      )
      updateCarouselArrowsVisibility()
    } else if (route.params.groupSlug) {
      await PostState.fetchPinnedPostsForHome({
        limit: 4,
        previousPaginationKeyValue: lastPinnedPostIndexValue,
        sort: 'descending',
      })
      updateCarouselArrowsVisibility()
    }
    return
  } catch (error) {
    console.error(error)
  }
}

async function loadComments(post: IPosts) {
  post.commentsAvatarGroupOption = reactive([])
  post.isLoadingComments = true
  if (post?._id && post?.commentsCount && post?.commentsCount > 0) {
    const postComments = await CommentState.fetchComments(post._id, {
      limit: 10,
    })
    let uniqueUsers = {}
    if (postComments) {
      uniqueUsers = postComments.reduce(
        (agg: { [key: string]: any }, comment: any) => {
          if (comment.user?.slug) {
            agg[comment.user?.slug] = comment.user
          }
          return agg
        },
        {}
      )
    }

    post.commentsAvatarGroupOption = Object.values(uniqueUsers)
  }
  post.isLoadingComments = false
}

const authorRole = computed(() => {
  return GroupState.state.groupAdmins.get(
    UserState.state.currentUser?.contactId
  )?.role
})

const mentionedUserTooltip = computed(() => {
  return document.getElementById('user_profile_tooltip')
})

function cancelCallback() {
  enableCreatePost.value = false
}

async function createPost() {
  cancelCallback()
}

function handleLiveStream() {
  showLiveStreamModal.value = true
}

function goLiveWithWebCam() {
  if (Capacitor.isNativePlatform()) {
    const platform = Capacitor.getPlatform()
    if (
      platform === 'android' &&
      (Number.isNaN(AppVersionsInstance.state.currentVersionCode) ||
        AppVersionsInstance.state.currentVersionCode <= 0)
    ) {
      showUpdateModal.value = true
      return
    }
  }
  showLiveStreamModalWithWebCam.value = true
}

function checkCarousalDragEnabled() {
  if (carousel.value == null) return
  carousalDragEnabled.value = !app.state.isMobile
}

function updateCarouselArrowsVisibility() {
  if (carousel.value == null) return
  // the carousal component has a bug that in some cases even if the scrollWidth is equal to clientWidth,
  // it still has some value in translateX which i overrided to 0 here
  if (carousel.value.clientWidth == carousel.value.scrollWidth) {
    showCarousalRightArrow.value = false
    showCarousalLeftArrow.value = false
    carousel.value.style.transform = 'translateX(0px)'
  }
  // while updating the arrows visiblity, we need to update the carousalDragEnabled value
  checkCarousalDragEnabled()

  // caclulate xTranslation as the carousel is translated in x direction
  const transformStyle = window
    .getComputedStyle(carousel.value)
    .getPropertyValue('transform')
  const transformMatrix = new WebKitCSSMatrix(transformStyle)
  const xTranslation = transformMatrix.m41

  if (xTranslation >= -5) {
    showCarousalLeftArrow.value = false
  } else {
    showCarousalLeftArrow.value = true
  }
  const totalScroll = xTranslation - carousel.value.clientWidth

  // if we have reached to end of the carousel, hide the right arrow
  if (totalScroll + carousel.value.scrollWidth <= 5) {
    showCarousalRightArrow.value = false
  } else {
    showCarousalRightArrow.value = true
  }
}
async function trackPinnedPostCarousalIndex(
  currentIndex: number,
  _lastIndex?: number
) {
  currentCarousalIndex.value = currentIndex

  setTimeout(() => {
    updateCarouselArrowsVisibility()
  }, 320)
  if (pinnedPosts.value && pinnedPosts.value.length > 0) {
    if (pinnedPosts.value.length - currentIndex <= 3) {
      // fetch next set of pinned posts
      if (router.currentRoute.value.name == 'channel-overview') {
        await getPinnedPostData(
          pinnedPosts.value[pinnedPosts.value.length - 1].channelPinnedIndex
        )
      } else if (router.currentRoute.value.name == 'home') {
        await getPinnedPostData(
          pinnedPosts.value[pinnedPosts.value.length - 1].homePinnedIndex
        )
      }
    }
  }
}
function toggleFeaturedPost() {
  showFeaturedPost.value = !showFeaturedPost.value
}
/** getting the current channel data  */
const channelData = computed(() => {
  const channelDataVal = GroupState?.state?.channelList?.find(
    ch => route.params.channelSlug === ch.slug
  )
  return channelDataVal
})

const routeToEvent = () => {
  router.push({
    name: 'events',
  })
}

const isChannelOverview = computed(() => route.name === 'channel-overview')

// Computed property to check if posts are ready to be rendered in UI
const isChannelDataReady = computed(() => {
  return !PostState.state.fetching && !isLoading.value
})

// Function to check if this is the position to show the "all set" message
const shouldShowAllSetMessage = (currentPost: IPosts, nextPost?: IPosts) => {
  if (
    !isChannelOverview.value ||
    !isChannelDataReady.value ||
    lastUnreadPostCount.value === 0
  ) {
    return false
  }

  const lastReadTime = new Date(lastChannelReadTimestamp.value!).getTime()
  const currentPostTime = new Date(currentPost.createdAt).getTime()

  // If this is the last post, check if it's before lastReadTimestamp
  if (!nextPost) {
    return currentPostTime > lastReadTime
  }

  // Show message if current post is before lastReadTimestamp
  // and next post is after lastReadTimestamp (transition point)
  const nextPostTime = new Date(nextPost.createdAt).getTime()
  return currentPostTime > lastReadTime && nextPostTime <= lastReadTime
}

const slidesPerView = computed(() => {
  if (pinnedPosts.value?.length === 1) return 1
  if (app.state.isMobile) return 1
  return 'auto'
})

const showCarouselArrow = computed(() => {
  if (!pinnedPosts.value || pinnedPosts.value.length === 0) return () => false
  return (direction: 'prev' | 'next') => {
    // For left arrow (prev)
    if (direction === 'prev') return currentCarouselIndex.value > 0
    // For right arrow (next)
    const totalItems = pinnedPosts.value?.length ?? 0
    const adjustedTotal = areMorePinnedPostAvailable.value
      ? totalItems + 1
      : totalItems
    return (
      currentCarouselIndex.value < adjustedTotal - (app.state.isMobile ? 1 : 2)
    )
  }
})

const isCarouselDraggable = computed(() => {
  return pinnedPosts.value && pinnedPosts.value.length > 1
})

async function handleCarouselChange(currentIndex: number) {
  currentCarouselIndex.value = currentIndex
  if (
    pinnedPosts.value &&
    pinnedPosts.value.length - currentIndex <= 3 &&
    areMorePinnedPostAvailable.value
  ) {
    const lastPost = pinnedPosts.value[pinnedPosts.value.length - 1]
    if (router.currentRoute.value.name === 'channel-overview') {
      if (lastPost?.channelPinnedIndex !== undefined) {
        await getPinnedPostData(lastPost.channelPinnedIndex)
      }
    } else if (router.currentRoute.value.name === 'home') {
      if (lastPost?.homePinnedIndex !== undefined) {
        await getPinnedPostData(lastPost.homePinnedIndex)
      }
    }
  }
}

// Feature flag, change true to false
const isLiveStreamAllowed = computed(() => {
  return GroupState.state.isGroupAdmin
})
</script>
<template>
  <div v-if="isMobile()" :class="showPostEditor ? '' : 'mt-2'" />
  <div>
    <div
      v-if="isMobile() && channelData?.name"
      class="relative flex items-center justify-between rounded-xl pt-1 text-xl font-normal text-communities-font-secondary"
    >
      <div
        class="flex flex-1 cursor-pointer flex-row bg-communities-background px-3 py-3 text-base shadow-sm md:mb-5 md:rounded-md"
      >
        <div class="flex-1 pl-2 pt-1">
          <UITextMdMedium class="!font-medium text-communities-font-primary">
            {{ truncateString(channelData?.name, 25) }}
          </UITextMdMedium>
        </div>

        <div class="flex-2 text-center">
          <div
            class="flex w-[9rem] items-center justify-center gap-1 rounded-full bg-gray-200 px-1 py-1"
          >
            <div
              v-if="channelData?.visibility == 'PUBLIC'"
              class="flex items-center"
            >
              <Globe01Icon class="h-4 w-4 font-bold text-gray-500" />
              <UITextLgMedium class="pl-1 !text-sm text-gray-700">
                {{ t('post.publicChannel') }}
              </UITextLgMedium>
            </div>
            <div v-else class="flex items-center">
              <Lock03Icon class="h-4 w-4 font-bold text-gray-500" />
              <UITextLgMedium class="pl-1 !text-sm text-gray-700">
                {{ t('post.privateChannel') }}
              </UITextLgMedium>
            </div>
          </div>
        </div>
      </div>
    </div>

    <div
      v-if="upcomingEvent?.title"
      class="mb-4"
      :class="isMobile() && 'mx-2 pt-4'"
    >
      <NAlert
        id="upcoming-event-alert"
        :type="'success'"
        :bordered="false"
        class="border-1 border-solid border-[--warning-300] bg-[--warning-25]"
        :closable="false"
        :show-icon="false"
      >
        <UITextMdNormal
          class="ml-2 mr-2 mt-[-2px] flex w-full cursor-default items-center gap-[4px] pr-4 text-[--warning-700]"
        >
          <div class="flex min-w-0 flex-1 items-center">
            <span
              tabindex="0"
              role="link"
              :aria-label="t('post.upcomingEvent')"
              class="cursor-pointer truncate font-semibold underline"
              @keyup.enter="routeToEvent"
              @click="routeToEvent"
              >{{ upcomingEvent?.title }}</span
            >
            <span class="ml-1 flex-shrink-0">{{
              upcomingEvent?.startTime
            }}</span>
          </div>
        </UITextMdNormal>
      </NAlert>
    </div>
    <div ref="postContainer">
      <div v-if="showPostEditor && !app.state.isMobile" class="mb-5">
        <div
          ref="postEditorTrigger"
          class="flex items-center justify-between gap-2 rounded-xl text-lg font-medium text-communities-font-secondary"
        >
          <div
            v-if="
              ChannelState.state.hasAnnouncementRestriction &&
              route.name !== 'home'
            "
            id="announcement-channel-restriction-info"
            class="flex w-full cursor-pointer items-center gap-4 rounded-xl border border-solid bg-communities-background px-4 py-[10px] text-sm text-communities-tertiary antialiased shadow-sm"
            tabindex="0"
          >
            <InfoCircleIcon class="h-5 w-5" />
            {{ t('post.onlyAdminsCanPost') }}
          </div>
          <div
            v-else
            id="create-post__trigger"
            class="flex w-full min-w-[200px] cursor-pointer items-center gap-4 rounded-xl border border-solid border-communities-border bg-communities-background px-4 py-2 text-lg antialiased shadow-sm"
            :class="isMobile() ? 'my-3 py-2' : ''"
            tabindex="0"
            role="textbox"
            :aria-label="t('post.createPost')"
            @click="enableCreatePost = true"
            @keyup.enter="enableCreatePost = true"
          >
            <BadgedAvatar
              :avatar-url="UserState.state.currentUser?.avatar"
              :full-name="UserState.state.currentUser?.fullName"
              :show-badge="false"
              :role="authorRole"
              :size="isMobile() ? 'large' : 30"
            />
            <div>
              {{ t('createPost.placeholder') }},
              {{ getFirstName(UserState.state.currentUser?.fullName!) }}?
            </div>
          </div>

          <button
            v-if="isLiveStreamAllowed"
            class="flex h-[49px] w-auto cursor-pointer items-center gap-2 rounded-lg border border-solid border-communities-border bg-communities-background px-3 text-sm font-medium text-communities-font-primary shadow-sm"
            @click.stop="handleLiveStream"
          >
            <VideoRecorderIcon class="h-6 w-6 fill-[#B42318] text-[#B42318]" />
            <UITextSmMedium
              class="hidden whitespace-nowrap text-nowrap md:block"
            >
              {{ t('golive.golive') }}
            </UITextSmMedium>
          </button>
        </div>

        <PostEditor
          v-if="enableCreatePost"
          id="create-post__editor"
          :show-editor="enableCreatePost"
          @update:show="cancelCallback"
          @update:save="createPost"
        />
      </div>
      <div v-if="showPostEditor && app.state.isMobile">
        <div
          v-if="
            ChannelState.state.hasAnnouncementRestriction &&
            route.name !== 'home'
          "
          id="announcement-channel-restriction-info"
          class="mx-2 mb-3 flex gap-2 rounded-xl border border-solid px-4 py-[16px] text-xs font-semibold text-communities-tertiary"
          tabindex="0"
        >
          <InfoCircleIcon class="h-5 w-5" />
          {{ t('post.onlyAdminsCanPost') }}
        </div>
        <div
          v-else
          ref="postEditorTrigger"
          class="relative flex items-center justify-between rounded-xl bg-communities-background text-xl font-normal text-communities-font-secondary"
          :class="isMobile() ? 'my-2' : ''"
        >
          <div
            id="create-post__trigger"
            class="flex flex-1 cursor-pointer flex-row gap-2 bg-communities-background px-3 text-base shadow-sm md:mb-5 md:rounded-md"
            :class="isMobile() ? 'py-4' : ''"
            tabindex="0"
            role="textbox"
            :aria-label="t('post.createPost')"
            @click="enableCreatePost = true"
            @keyup.enter="enableCreatePost = true"
          >
            <div
              class="flex flex-1 items-center gap-2 rounded-lg border border-solid border-communities-border px-2 py-2"
            >
              <div class="z-10 box-border inline-block">
                <BadgedAvatar
                  :avatar-url="UserState.state.currentUser?.avatar"
                  :full-name="UserState.state.currentUser?.fullName"
                  :show-badge="false"
                  :role="authorRole"
                  :size="'medium'"
                />
              </div>
              <div
                class="font-600 ml-2.5 flex flex-1 flex-row items-center justify-start text-sm text-communities-font-secondary"
              >
                <span class="font-[600]">
                  {{ t('createPost.placeholder') }},
                  {{
                    getAbbreviatedText(
                      getFirstName(UserState.state.currentUser?.fullName!),
                      25
                    )
                  }}?
                </span>
              </div>
            </div>
            <button
              v-if="isLiveStreamAllowed"
              class="flex h-[49px] w-auto cursor-pointer items-center gap-2 rounded-lg border border-solid border-communities-border bg-communities-background px-3 text-sm font-medium text-communities-font-primary shadow-sm"
              @click.stop="goLiveWithWebCam"
            >
              <VideoRecorderIcon
                class="h-6 w-6 fill-[#B42318] text-[#B42318]"
              />
            </button>
          </div>
        </div>

        <PostEditor
          v-if="enableCreatePost"
          id="create-post__editor"
          :show-editor="enableCreatePost"
          @update:show="cancelCallback"
          @update:save="createPost"
        />
      </div>
      <div
        v-if="!showPostEditor && app.state.isMobile"
        :class="route.name == 'home' ? '' : 'mb-2 '"
      />

      <div
        id="post-container"
        ref="postContainerRef"
        class="mb-2 flex flex-col md:mb-3"
        :class="isMobile() ? 'bg-gray-100' : ''"
      >
        <div class="flex flex-1 flex-col">
          <UICard
            v-if="showPinnedPosts"
            class="featured_post_card mb-1.5 overflow-hidden !border-[0px] bg-communities-background md:mb-5 md:border"
          >
            <div class="flex items-center justify-between">
              <div class="flex items-center justify-center">
                <UITextLgMedium
                  class="pl-2 text-xl font-semibold text-communities-font-primary"
                  >{{ t('post.featured') }}</UITextLgMedium
                >
                <UITooltip v-if="!app.state.isMobile" trigger="click">
                  <UITextMdMedium>{{ t('post.featuredAbout') }}</UITextMdMedium>
                  <UITextSmNormal>{{
                    t('post.featuredAboutDescription')
                  }}</UITextSmNormal>

                  <template #trigger>
                    <div
                      class="flex h-9 w-9 items-center justify-center rounded-full hover:bg-communities-fill"
                    >
                      <InfoCircleIcon
                        class="h-[18px] w-[18px] cursor-pointer text-communities-font-secondary md:h-5 md:w-5"
                      />
                    </div>
                  </template>
                </UITooltip>
                <div
                  v-else
                  class="flex h-9 w-9 items-center justify-center rounded-full hover:bg-communities-fill"
                  tabindex="0"
                  role="button"
                  :aria-label="t('common.ariaLabels.aboutFeaturedPost')"
                  @click="showFeaturedPostAbout = !showFeaturedPostAbout"
                  @keyup.enter="showFeaturedPostAbout = !showFeaturedPostAbout"
                >
                  <InfoCircleIcon
                    class="h-[18px] w-[18px] cursor-pointer text-communities-font-secondary md:h-5 md:w-5"
                  />
                </div>
              </div>
              <div
                class="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full hover:bg-communities-fill"
                tabindex="0"
                role="button"
                :aria-label="t('common.ariaLabels.toggleFeaturedPost')"
                @click="toggleFeaturedPost"
                @keyup.enter="toggleFeaturedPost"
              >
                <ChevronUpIcon
                  v-if="showFeaturedPost"
                  class="h-6 w-6 text-communities-font-secondary"
                />
                <ChevronDownIcon
                  v-else
                  class="h-6 w-6 text-communities-font-secondary"
                />
              </div>
            </div>
            <div class="mx-1" :class="!showFeaturedPost && 'mb-2'">
              <div
                v-if="showFeaturedPost"
                class="relative mb-2 mt-2 max-h-[350px]"
              >
                <n-carousel
                  v-if="
                    !route.fullPath.includes('/users/') &&
                    pinnedPosts &&
                    pinnedPosts.length > 0
                  "
                  :draggable="isCarouselDraggable"
                  :loop="false"
                  :space-between="20"
                  :show-dots="false"
                  :show-arrow="true"
                  :slides-per-view="slidesPerView"
                  :centered="false"
                  @update:current-index="handleCarouselChange"
                >
                  <n-carousel-item
                    v-for="post in pinnedPosts"
                    :key="post._id"
                    :class="
                      app.state.isMobile
                        ? 'flex w-full justify-center'
                        : 'flex !w-[320px] justify-center'
                    "
                  >
                    <FeaturedPostCard
                      :mention-user-tooltip="mentionedUserTooltip"
                      :post="post"
                    />
                  </n-carousel-item>
                  <n-carousel-item
                    v-if="areMorePinnedPostAvailable"
                    :class="
                      app.state.isMobile
                        ? 'flex w-full justify-center'
                        : 'flex !w-[320px] justify-center'
                    "
                  >
                    <FeaturedPostCard
                      :mention-user-tooltip="mentionedUserTooltip"
                      :loading-card="true"
                    />
                  </n-carousel-item>
                  <template #arrow="{ prev, next }">
                    <div>
                      <button
                        id="carousal_btn_left"
                        class="nav_btn_shadow absolute -left-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-solid border-communities-border bg-communities-background text-communities-font-secondary hover:bg-communities-background md:flex"
                        :class="{
                          'cursor-not-allowed opacity-50':
                            !showCarouselArrow('prev'),
                        }"
                        @click="showCarouselArrow('prev') && prev()"
                      >
                        <ChevronLeftIcon class="h-7 w-7" />
                      </button>

                      <button
                        id="carousal_btn_right"
                        class="nav_btn_shadow absolute -right-0 top-1/2 hidden h-12 w-12 -translate-y-1/2 cursor-pointer items-center justify-center rounded-full border border-solid border-communities-border bg-communities-background font-normal text-communities-font-secondary hover:bg-communities-background md:flex"
                        :class="{
                          'cursor-not-allowed opacity-50':
                            !showCarouselArrow('next'),
                        }"
                        @click="showCarouselArrow('next') && next()"
                      >
                        <ChevronRightIcon class="h-7 w-7 font-normal" />
                      </button>
                    </div>
                  </template>
                </n-carousel>

                <!-- Loading state when no posts -->
                <div v-else class="flex w-auto flex-row gap-2 overflow-x-auto">
                  <div
                    v-for="index in 4"
                    :key="index"
                    style="width: 320px; flex-shrink: 0"
                  >
                    <FeaturedPostCard
                      :mention-user-tooltip="mentionedUserTooltip"
                      :loading-card="true"
                    />
                  </div>
                </div>
              </div>
            </div>
          </UICard>

          <div v-if="posts.length && posts.length > 0" class="mb-12 md:mb-0">
            <div
              v-for="(item, index) in posts"
              :key="item._id"
              :class="isMobile() ? 'pb-[1px]' : 'pb-2'"
            >
              <PostCard
                :mention-user-tooltip="mentionedUserTooltip"
                :post="item"
                :show-popover="showPopover"
                @load-comments="loadComments"
              />
              <!-- Show "all set" message at the transition point between unread and read posts -->
              <div
                v-if="shouldShowAllSetMessage(item, posts[index + 1])"
                class="all-set-divider mb-4 mt-6 flex items-center justify-center rounded-lg"
              >
                <UIDivider
                  title-placement="center"
                  :dashed="false"
                  :vertical="false"
                >
                  <div
                    class="flex items-center justify-center rounded-full border border-solid border-communities-font-secondary px-4"
                  >
                    <UITextMdNormal class="text-communities-font-primary">
                      {{ '🎊 ' + t('common.allSet') }}
                    </UITextMdNormal>
                  </div>
                </UIDivider>
              </div>
            </div>
          </div>
          <div v-if="isLoading">
            <!-- <UISpin size="large" class="min-w-48 flex h-[80vh] items-center" /> -->
            <!-- <UISpace vertical> -->
            <!-- <UISkeleton text :repeat="2" class="mb-4 h-60"></UISkeleton> -->

            <ShimmerLoader
              v-if="!posts.length"
              :repeat="1"
              :is-loading="true"
              :class-name="
                app.state.isMobile
                  ? 'mb-4 h-14 rounded-xl mx-5'
                  : 'mb-4 h-14 rounded-xl w-[100%]'
              "
            />
            <ShimmerLoader
              :repeat="4"
              :is-loading="true"
              :class-name="
                app.state.isMobile
                  ? 'mb-4 h-40 rounded-xl mx-5'
                  : 'mb-4 h-52 rounded-xl w-[100%]'
              "
            />
            <!-- </UISpace> -->
          </div>
        </div>
      </div>
      <div
        v-if="
          !posts.length &&
          !enableCreatePost &&
          !PostState.state.fetching &&
          !isLoading
        "
        class=""
      >
        <div
          class="no-posts-found flex w-full flex-col items-center justify-center"
        >
          <img :src="NO_POST_ICON" class="h-48 w-48" :alt="t('common.ariaLabels.noPostsFound')" />
          <UITextMdMedium>{{ t('post.noPostsFound') }}</UITextMdMedium>
        </div>
      </div>
      <router-view />
    </div>

    <UIDrawer
      id="post-options-drawer"
      v-model:show="showFeaturedPostAbout"
      class="rounded-t-xl p-0"
      :placement="'bottom'"
      :height="GroupState.state.isGroupAdmin ? 450 : 200"
    >
      <UIDrawerContent
        id="post-options-drawer-menu"
        v-swipe="{
          direction: 'down',
          callback: () => (showFeaturedPostAbout = !showFeaturedPostAbout),
        }"
        class="p-0"
        :closable="false"
      >
        <div class="flex flex-col items-center justify-center px-4 py-2">
          <div
            class="mb-6 h-1 w-16 rounded-md border border-solid border-communities-border bg-communities-border"
          />
          <div class="w-8">
            <Pin02Icon
              class="mb-2 h-7 w-7 fill-communities-font-secondary text-communities-font-secondary"
            />
          </div>

          <UITextLgMedium
            class="text-xl font-bold text-communities-font-primary"
            >{{ t('post.featuredAbout') }}</UITextLgMedium
          >

          <UITextLgMedium
            v-if="GroupState.state.isGroupAdmin"
            class="mb-6 text-center text-base font-normal text-communities-font-primary"
            >{{ t('post.pinInfo') }}</UITextLgMedium
          >
          <UITextLgMedium
            v-else
            class="mb-6 text-center font-normal text-communities-font-primary"
            >{{ t('post.featuredAboutDescription') }}</UITextLgMedium
          >
          <div v-if="GroupState.state.isGroupAdmin">
            <div class="mb-4 flex items-center justify-center">
              <div class="w-8"><EyeIcon class="mr-3 h-6 w-6" /></div>

              <UITextLgMedium
                class="font-normal leading-6 text-communities-font-primary"
              >
                {{ t('post.pinInfoEveryone') }}</UITextLgMedium
              >
            </div>
            <div class="mb-4 flex items-center justify-center">
              <ClockIcon class="mr-3 h-[26px] w-[26px]" />
              <UITextLgMedium
                class="font-normal leading-6 text-communities-font-primary"
                >{{ t('post.pinInfoRemainVisible') }}</UITextLgMedium
              >
            </div>
            <div class="mb-4 flex items-center justify-center">
              <div class="w-8"><MoveIcon class="mr-3 h-5 w-5" /></div>

              <UITextLgMedium
                class="font-normal leading-6 text-communities-font-primary"
                >{{ t('post.pinInfoNewestItem') }}</UITextLgMedium
              >
            </div>
            <UITextSmNormal
              class="text-center text-communities-font-secondary"
              >{{ t('post.onlyVisibleToAdmins') }}</UITextSmNormal
            >
          </div>
        </div>
      </UIDrawerContent>
    </UIDrawer>
  </div>

  <LiveStreamModal
    v-if="showLiveStreamModal"
    :show="showLiveStreamModal"
    @update:show="showLiveStreamModal = false"
  />

  <WebCamLiveModal
    v-if="showLiveStreamModalWithWebCam"
    :show="showLiveStreamModalWithWebCam"
    @update:show="showLiveStreamModalWithWebCam = false"
  />

  <CommunityBottomSheet v-model="showUpdateModal" class="z-[10000]">
    <div class="flex justify-end px-6">
      <XCloseIcon
        class="h-6 w-6 cursor-pointer text-communities-font-secondary"
        @click="showUpdateModal = false"
      />
    </div>
    <div
      class="flex h-[calc(100dvh-150px)] flex-col items-center justify-center px-6 pb-8 pt-4"
    >
      <UITextLgMedium class="text-communities-font-primary"
        >A new version of the app is available. Please update to use this
        feature!</UITextLgMedium
      >
      <UIButton
        id="update-on-play-store__btn"
        type="primary"
        size="large"
        class="mt-4 w-full text-gray-100"
        @click="openPlayStore"
      >
        Update
      </UIButton>
    </div>
  </CommunityBottomSheet>
</template>

<style scoped>
:deep(.n-divider .n-divider__title) {
  margin-left: 0px !important;
  margin-right: 0px !important;
}
:deep(.all-set-divider .n-divider .n-divider__line) {
  background-color: var(--fontSecondary) !important;
}
:deep(.n-alert) {
  border-radius: 12px !important;
}
.no-posts-found {
  height: calc(100vh - 155px);
}
:deep(.n-carousel) {
  height: 360px !important;
}
.hl-card:deep(.hl-card-content) {
  padding: 11px 12px 2px 12px !important;
}

.post_content {
  width: 82.5%;
}

.fade-enter-active,
.fade-leave-active {
  transition: opacity 0.3s, transform 0.3s;
}

.nav_btn_shadow {
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06),
    0px 4px 8px -2px rgba(16, 24, 40, 0.1) !important;
  padding: 0px !important;
}

.fade-enter,
.fade-leave-to {
  opacity: 0;
  transform: translateY(20px);
}
.n-divider:not(.n-divider--vertical) {
  margin-top: 0px;
  margin-bottom: 0px;
}
.featured_post_card {
  box-shadow: none !important;
  border-radius: 10px;
}
.featured_post_card:hover {
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06),
    0px -1px 8px -2px rgba(16, 24, 40, 0.1) !important;
}

.custom-arrow {
  position: absolute;
  top: 50%;
  transform: translateY(-50%);
  width: 100%;
  display: flex;
  justify-content: space-between;
  padding: 0 -12px;
  pointer-events: none;
}

.custom-arrow button {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 40px;
  height: 40px;
  color: var(--communities-font-secondary);
  background-color: var(--communities-background);
  border: 1px solid var(--communities-border);
  border-radius: 50%;
  transition: all 0.3s cubic-bezier(0.4, 0, 0.2, 1);
  cursor: pointer;
  pointer-events: auto;
  box-shadow: 0px 2px 4px -2px rgba(16, 24, 40, 0.06),
    0px 4px 8px -2px rgba(16, 24, 40, 0.1);
}

.custom-arrow--left {
  transform: translateX(-20px);
}

.custom-arrow--right {
  transform: translateX(20px);
}

.custom-arrow button:hover {
  background-color: var(--communities-fill);
}

.custom-arrow button:active {
  transform: scale(0.95) translateX(-20px);
}

.custom-arrow--right:active {
  transform: scale(0.95) translateX(20px);
}

@media (max-width: 930px) {
  .featured_post_card {
    border-radius: 0px;
  }

  .hl-card:deep(.hl-card-content) {
    padding: 7px 8px 0px 8px !important;
  }

  :deep(.n-carousel-item) {
    width: 100% !important;
  }
}
</style>
